import type {
  FiscalFact,
  FiscalFactFieldName,
  FiscalFactFields,
  FiscalFactStatus,
  FiscalFactVerification,
} from "@backchannel/tax-contracts";
import { ufSchema } from "@backchannel/tax-contracts";

/**
 * Presentation helpers for Regras → Verificação and the shared certification
 * badge. Pure functions only: facts come from the fiscal facts admin API.
 */

export type Uf = (typeof ufSchema.options)[number];
export const ALL_UFS = ufSchema.options as readonly Uf[];

/** Every badge the platform shows for a fiscal value, strongest first. */
export type CertificationBadgeKind =
  | "PUBLISHED_RULE"
  | "CERTIFIED"
  | "VERIFIED"
  | "UNVERIFIED"
  | "EXPIRED"
  | "CONFLICTING"
  | "REJECTED"
  | "SUPERSEDED";

export const CERTIFICATION_BADGE_KINDS: readonly CertificationBadgeKind[] = [
  "PUBLISHED_RULE",
  "CERTIFIED",
  "VERIFIED",
  "UNVERIFIED",
  "EXPIRED",
  "CONFLICTING",
  "REJECTED",
  "SUPERSEDED",
];

export function badgeKindForStatus(status: FiscalFactStatus): CertificationBadgeKind {
  return status;
}

export type VerificationAction = FiscalFactVerification["action"];

/**
 * Actions the API accepts for a fact in its current state (a disallowed
 * transition answers 409, so the page never offers it).
 */
export function verificationActions(fact: FiscalFact): VerificationAction[] {
  if (fact.status === "REJECTED") return [];
  const confirmable =
    fact.status === "UNVERIFIED" ||
    fact.status === "CONFLICTING" ||
    ((fact.status === "CERTIFIED" || fact.status === "VERIFIED") && fact.revalidationRequested);
  return [
    ...(confirmable ? (["CONFIRM"] as const) : []),
    "REJECT",
    ...(fact.revalidationRequested ? [] : (["REQUEST_IOB"] as const)),
  ];
}

export const FACT_FIELDS: readonly FiscalFactFieldName[] = [
  "stApplicable",
  "originalMva",
  "destinationInternalRate",
  "interstateRate",
];

export function formatNcmCode(ncm: string): string {
  return ncm.length === 8 ? `${ncm.slice(0, 4)}.${ncm.slice(4, 6)}.${ncm.slice(6)}` : ncm;
}

/** A citation host is allowlisted when the worker matched it to a reviewed source. */
export function isAllowlisted(citation: { sourceKey: string | null }): boolean {
  return citation.sourceKey !== null;
}

/** Hosts of a fact's citations, de-duplicated in order. */
export function citationHosts(fact: Pick<FiscalFact, "citations">): string[] {
  return [...new Set(fact.citations.map((citation) => citation.host))];
}

/** Field values that disagree between two facts (only fields both state). */
export function differingFields(left: FiscalFactFields, right: FiscalFactFields) {
  return FACT_FIELDS.filter((field) => {
    const a = left[field];
    const b = right[field];
    if (a === undefined || b === undefined) return false;
    if (field === "stApplicable") return a !== b;
    return Number(a) !== Number(b);
  });
}

export type BootstrapCell = "CONFIRMED" | "PENDING" | "CONFLICT" | "NONE";

/**
 * The 27 UF modal internal rates: the strongest usable UF-level fact per UF.
 * NCM- or CEST-specific internal rates are not part of the bootstrap.
 */
export function bootstrapInternalRates(
  facts: FiscalFact[],
): Map<Uf, { cell: BootstrapCell; fact: FiscalFact | null }> {
  const rank: Record<BootstrapCell, number> = { CONFIRMED: 3, CONFLICT: 2, PENDING: 1, NONE: 0 };
  const cellOf = (fact: FiscalFact): BootstrapCell =>
    fact.status === "CERTIFIED" || fact.status === "VERIFIED"
      ? "CONFIRMED"
      : fact.status === "UNVERIFIED"
        ? "PENDING"
        : fact.status === "CONFLICTING"
          ? "CONFLICT"
          : "NONE";
  const result = new Map<Uf, { cell: BootstrapCell; fact: FiscalFact | null }>(
    ALL_UFS.map((uf) => [uf, { cell: "NONE", fact: null }]),
  );
  for (const fact of facts) {
    if (fact.kind !== "INTERNAL_RATE") continue;
    const { match } = fact;
    if (match.ncm || match.cest || match.ncmPrefixes?.length) continue;
    const uf = match.destinationUf;
    if (!uf) continue;
    const cell = cellOf(fact);
    const current = result.get(uf);
    if (cell !== "NONE" && (!current || rank[cell] > rank[current.cell])) {
      result.set(uf, { cell, fact });
    }
  }
  return result;
}

/** 27 × 26 origin → destination pairs make the interstate table. */
export const INTERSTATE_PAIRS = ALL_UFS.length * (ALL_UFS.length - 1);

/** Percent strings a source may print for a decimal rate (pt-BR and plain). */
export function percentVariants(rate: string): string[] {
  const value = Number(rate) * 100;
  if (!Number.isFinite(value)) return [];
  const out = new Set<string>();
  for (const digits of [0, 1, 2]) {
    const fixed = value.toFixed(digits);
    if (Number(fixed) !== Number(value.toFixed(4))) continue;
    out.add(`${fixed.replace(".", ",")}%`);
    out.add(`${fixed.replace(".", ",")} %`);
    out.add(`${fixed}%`);
  }
  return [...out];
}

/** Strings worth highlighting in a quote: codes and the fact's values. */
export function quoteNeedles(fact: Pick<FiscalFact, "match" | "fields">): string[] {
  const needles = new Set<string>();
  const { match, fields } = fact;
  if (match.ncm) {
    needles.add(formatNcmCode(match.ncm));
    needles.add(match.ncm);
  }
  if (match.cest) needles.add(match.cest);
  for (const prefix of match.ncmPrefixes ?? []) {
    if (prefix.length >= 4) needles.add(prefix.length === 8 ? formatNcmCode(prefix) : prefix);
  }
  for (const field of ["originalMva", "destinationInternalRate", "interstateRate"] as const) {
    const value = fields[field];
    if (value) for (const variant of percentVariants(value)) needles.add(variant);
  }
  return [...needles].filter((needle) => needle.length >= 2);
}

export type QuotePart = { text: string; mark: boolean };

/** Splits a quote into plain and highlighted parts (longest needle wins). */
export function highlightQuote(quote: string, needles: string[]): QuotePart[] {
  const sorted = [...needles].sort((a, b) => b.length - a.length);
  const parts: QuotePart[] = [];
  let index = 0;
  let plain = "";
  const lower = quote.toLocaleLowerCase("pt-BR");
  while (index < quote.length) {
    const hit = sorted.find((needle) => lower.startsWith(needle.toLocaleLowerCase("pt-BR"), index));
    if (hit) {
      if (plain) parts.push({ text: plain, mark: false });
      plain = "";
      parts.push({ text: quote.slice(index, index + hit.length), mark: true });
      index += hit.length;
    } else {
      plain += quote[index];
      index += 1;
    }
  }
  if (plain) parts.push({ text: plain, mark: false });
  return parts;
}
